import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  TrendingUp, TrendingDown, Building2, Users,
  AlertTriangle, Info, CircleDollarSign, ArrowUpRight,
  Clock, Shield, Zap, Activity
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from "recharts";
import axios from "axios";
import { API, useAuth } from "../contexts/AuthContext";
import { formatPYG, formatPYGCompact, formatDate, getStatusColor, getProgressColor } from "../utils/format";
import { Skeleton } from "../components/ui/skeleton";
import JefeComprasDashboard from "./dashboard/JefeComprasDashboard";
import JefeLogisticaDashboard from "./dashboard/JefeLogisticaDashboard";
import DetalleDashboardModal from "./dashboard/DetalleDashboardModal";   // Fix CS (2026-09-14)
import TareasPanel from "../components/TareasPanel";   // Fix DO (2026-09-22)

// Fix DO (2026-09-22): "donde actualmente está ALERTAS, debemos dar un uso tal que desde
// allí se pueda ver absolutamente todo el movimiento y tareas ejecutadas, avisos y alertas
// dentro del ERP en tiempo real" (CEN, 21/09). Una sola lista con las tareas, los avisos y
// lo que registra la auditoría, refrescada sola cada 20 segundos.
const TONO_MOV = { ok: "text-emerald-600", warn: "text-amber-600", err: "text-red-600", info: "text-gray-500" };
const TONO_SEVERIDAD = { err: "error", warn: "warning", ok: "info", info: "info" };

function MovimientosFeed() {
  const { user } = useAuth();
  const [movs, setMovs] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let vivo = true;
    const traer = () => axios.get(`${API}/actividad/feed?limit=40`, { withCredentials: true })
      .then(({ data }) => { if (vivo) { setMovs(Array.isArray(data?.movimientos) ? data.movimientos : []); setError(false); } })
      .catch(() => { if (vivo) setError(true); })
      .finally(() => { if (vivo) setCargando(false); });
    traer();
    const t = setInterval(traer, 20000);
    return () => { vivo = false; clearInterval(t); };
  }, []);

  const hora = (at) => {
    const d = new Date(at);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString("es-PY", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 fade-in-up stagger-4" data-testid="movimientos-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-gray-900 text-[15px]">Movimientos del sistema en tiempo real</h3>
        <span className="flex items-center gap-1.5 text-[11px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> EN VIVO
        </span>
      </div>
      {cargando ? (
        <div className="space-y-2">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10 w-full rounded-lg" />)}</div>
      ) : error ? (
        <p className="text-sm text-gray-400 py-8 text-center" data-testid="movimientos-error">No se pudieron cargar los movimientos.</p>
      ) : movs.length === 0 ? (
        <p className="text-sm text-gray-400 py-8 text-center" data-testid="movimientos-vacio">Todavía no hay movimientos registrados.</p>
      ) : (
        <div className="max-h-[420px] overflow-y-auto -mx-1">
          <table className="w-full text-[11px]">
            <thead className="sticky top-0 bg-white">
              <tr className="text-gray-400 uppercase text-[10px] font-semibold">
                <th className="text-left px-1 pb-2">Cuándo</th>
                <th className="text-left px-1 pb-2">Tipo</th>
                <th className="text-left px-1 pb-2">Detalle</th>
                <th className="text-left px-1 pb-2">Estado</th>
                <th className="text-left px-1 pb-2">Ejecutor</th>
              </tr>
            </thead>
            <tbody>
              {movs.map((m, i) => (
                <tr key={m.id || i} className="border-t border-gray-50" data-testid="movimiento-item">
                  <td className="px-1 py-2 text-gray-400 whitespace-nowrap">{hora(m.at)}</td>
                  <td className="px-1 py-2 text-gray-600 whitespace-nowrap">{m.tipo}{m.modulo ? ` · ${m.modulo}` : ""}</td>
                  <td className="px-1 py-2 text-gray-800">
                    <span className="inline-flex items-start gap-1.5">
                      {m.tipo === "Alerta" && <AlertIcon severity={TONO_SEVERIDAD[m.tono] || "info"} />}
                      {m.detalle}
                    </span>
                  </td>
                  <td className="px-1 py-2 whitespace-nowrap">
                    <span className={m.tipo === "Alerta"
                      ? `px-1.5 py-0.5 rounded-lg border font-semibold ${severityBg[TONO_SEVERIDAD[m.tono]] || severityBg.info}`
                      : `font-semibold ${TONO_MOV[m.tono] || TONO_MOV.info}`}>{m.estado}</span>
                  </td>
                  <td className="px-1 py-2 text-gray-500 whitespace-nowrap">{m.ejecutor}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {user?.role === "Superadministrador" && (
        <Link to="/auditoria" className="mt-3 block text-center text-xs font-semibold text-[#2E6DA4] hover:underline" data-testid="movimientos-ver-todo">
          Ver registro completo
        </Link>
      )}
    </div>
  );
}

// Fix CS (2026-09-14): con `onAbrir` la tarjeta pasa a ser un botón: al tocarla se abre
// el detalle de dónde sale ese número. Sin `onAbrir` se comporta igual que siempre.
function KPICard({ title, value, subtitle, icon: Icon, trend, trendUp, color, loading, testId, delay, onAbrir }) {
  const colorMap = {
    blue: { bg: "bg-blue-50", text: "text-[#2E6DA4]", border: "border-blue-100/80", accent: "#2E6DA4" },
    emerald: { bg: "bg-emerald-50", text: "text-emerald-600", border: "border-emerald-100/80", accent: "#10B981" },
    red: { bg: "bg-red-50", text: "text-red-500", border: "border-red-100/80", accent: "#EF4444" },
    purple: { bg: "bg-purple-50", text: "text-purple-600", border: "border-purple-100/80", accent: "#8B5CF6" },
  };
  const c = colorMap[color] || colorMap.blue;

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <Skeleton className="h-4 w-24 mb-3" />
        <Skeleton className="h-8 w-32 mb-2" />
        <Skeleton className="h-3 w-20" />
      </div>
    );
  }

  return (
    <div
      onClick={onAbrir}
      role={onAbrir ? "button" : undefined}
      tabIndex={onAbrir ? 0 : undefined}
      onKeyDown={onAbrir ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onAbrir(); } } : undefined}
      className={`bg-white rounded-2xl border border-gray-100 p-5 card-hover fade-in-up stagger-${delay || 1} relative overflow-hidden group ${onAbrir ? "cursor-pointer hover:border-gray-200" : ""}`}
      data-testid={testId}
    >
      {/* Subtle accent line on top */}
      <div className="absolute top-0 left-0 right-0 h-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: c.accent }} />

      <div className="flex items-start justify-between mb-3">
        <p className="text-[13px] font-medium text-gray-500">{title}</p>
        <div className={`p-2 rounded-xl ${c.bg} ${c.border} border`}>
          <Icon size={16} className={c.text} />
        </div>
      </div>
      <p className="text-[26px] font-bold text-gray-900 tracking-tight leading-none">{value}</p>
      {subtitle && <p className="text-[13px] text-gray-400 mt-1.5">{subtitle}</p>}
      {trend && (
        <div className={`flex items-center gap-1 mt-2.5 text-xs font-semibold ${trendUp ? "text-emerald-600" : "text-red-500"}`}>
          {trendUp ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
          {trend}
        </div>
      )}
    </div>
  );
}

const AlertIcon = ({ severity }) => {
  if (severity === "error") return <CircleDollarSign size={15} className="text-red-500 flex-shrink-0" />;
  if (severity === "warning") return <AlertTriangle size={15} className="text-amber-500 flex-shrink-0" />;
  return <Info size={15} className="text-blue-500 flex-shrink-0" />;
};

const severityBg = {
  error: "bg-red-50 border-red-100",
  warning: "bg-amber-50 border-amber-100",
  info: "bg-blue-50 border-blue-100",
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-xl shadow-xl px-4 py-3 text-sm">
        <p className="font-semibold text-gray-800 mb-2">{label}</p>
        {payload.map((p) => (
          <p key={p.name} style={{ color: p.color }} className="flex justify-between gap-6">
            <span className="text-gray-500">{p.name === "ingresos" ? "Ingresos" : "Gastos"}</span>
            <span className="font-bold">Gs. {p.value.toFixed(1)}M</span>
          </p>
        ))}
        {payload.length === 2 && (
          <p className="border-t border-gray-100 mt-2 pt-2 text-gray-600 flex justify-between">
            <span>Neto</span>
            <span className={`font-bold ${payload[0].value - payload[1].value >= 0 ? "text-emerald-600" : "text-red-500"}`}>
              Gs. {(payload[0].value - payload[1].value).toFixed(1)}M
            </span>
          </p>
        )}
      </div>
    );
  }
  return null;
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // BLOQUE 34 — el Dashboard de "Jefe de Compras" y "Jefe de Logística" es
  // 100% distinto (paneles operativos propios) — NO se toca el dashboard
  // genérico del resto de los roles (Superadmin, Administrador, etc.). El
  // fetch de /dashboard/stats (genérico) se omite para estos 2 roles, pero el
  // hook se sigue llamando siempre (regla de hooks) — el return early va
  // DESPUÉS de todos los hooks.
  const isJefeCompras = user?.role === "Jefe de Compras";
  const isJefeLogistica = user?.role === "Jefe de Logística";

  // Financial data is sensitive: only these roles can see it
  const FINANCE_ROLES = ["Superadministrador", "Administrador", "Gerente Tecnico", "Contador",
    "Director", "Gerencia Técnica", "Gerencia Financiera", "Accionista", "Gerencia Comercial"];
  const canSeeFinance = FINANCE_ROLES.includes(user?.role);
  const [detalle, setDetalle] = useState(null);   // Fix CS (2026-09-14): ingresos | gastos | proyectos | personal

  useEffect(() => {
    if (isJefeCompras || isJefeLogistica) { setLoading(false); return; }
    axios.get(`${API}/dashboard/stats`, { withCredentials: true })
      .then(({ data }) => setStats(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isJefeCompras, isJefeLogistica]);

  if (isJefeCompras) return <JefeComprasDashboard />;
  if (isJefeLogistica) return <JefeLogisticaDashboard />;

  const today = new Date().toLocaleDateString("es-PY", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  const firstName = user?.name?.split(" ")[0] || "Usuario";
  const isSuperAdmin = user?.role === "Superadministrador";

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-[1440px] mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2 fade-in-up">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight" data-testid="dashboard-greeting">
            Hola, {firstName}
          </h1>
          <p className="text-sm text-gray-400 mt-1 capitalize">{today}</p>
        </div>
        {stats?.pending_alerts > 0 && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200/80 rounded-xl px-3.5 py-2 text-amber-700 text-sm font-medium shadow-sm" data-testid="pending-alerts-badge">
            <AlertTriangle size={15} />
            {stats.pending_alerts} alerta{stats.pending_alerts > 1 ? "s" : ""} pendiente{stats.pending_alerts > 1 ? "s" : ""}
          </div>
        )}
      </div>

      {/* Superadmin Control Panel */}
      {isSuperAdmin && (
        <div className="relative overflow-hidden bg-gradient-to-br from-[#1A3D6B] via-[#1E5088] to-[#2E6DA4] rounded-2xl p-5 sm:p-7 text-white shadow-xl shadow-[#2E6DA4]/10 fade-in-up stagger-1" data-testid="superadmin-panel">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/[0.04] rounded-full -translate-y-1/2 translate-x-1/4" />
          <div className="absolute bottom-0 left-1/3 w-40 h-40 bg-white/[0.03] rounded-full translate-y-1/2" />
          <div className="absolute top-8 right-16 w-2 h-2 rounded-full bg-amber-400/50" />
          <div className="absolute bottom-12 left-12 w-1.5 h-1.5 rounded-full bg-blue-300/40" />

          <div className="relative z-10">
            <div className="flex items-center gap-2.5 mb-2">
              <div className="p-1.5 rounded-lg bg-amber-400/20 backdrop-blur-sm">
                <Shield size={16} className="text-amber-300" />
              </div>
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-300/90">Control Total del Sistema</span>
            </div>
            <p className="text-xl sm:text-2xl font-bold mb-4 tracking-tight">Panel de Superadministrador</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { val: stats?.total_workers || 0, label: "Personal activo", icon: Users },
                { val: stats?.total_projects || 0, label: "Proyectos totales", icon: Building2 },
                { val: stats?.active_projects || 0, label: "En ejecucion", icon: Activity },
                { val: stats?.pending_alerts || 0, label: "Alertas activas", icon: AlertTriangle },
              ].map((item, i) => (
                <div key={i} className="bg-white/[0.08] backdrop-blur-sm rounded-xl px-3.5 py-3 border border-white/[0.08] hover:bg-white/[0.12] transition-colors">
                  <div className="flex items-center gap-2 mb-1">
                    <item.icon size={13} className="text-blue-200/70" />
                    <p className="text-[26px] font-bold leading-none">{item.val}</p>
                  </div>
                  <p className="text-[11px] text-blue-200/70 font-medium">{item.label}</p>
                </div>
              ))}
            </div>
            <p className="text-[12px] text-blue-200/50 mt-4 font-medium">
              Visibilidad completa sobre usuarios, operaciones y movimientos financieros.
            </p>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        {canSeeFinance && (
          <KPICard
            title="Ingresos Totales"
            value={loading ? "..." : formatPYGCompact(stats?.total_revenue)}
            subtitle={loading ? "" : (stats?.revenue_usd
              /* Fix FG (2026-10-08): lo que entró en USD, ya convertido, y el cambio de hoy. */
              ? `Incluye US$ ${Number(stats.revenue_usd).toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (${formatPYGCompact(stats.revenue_usd_pyg)} Gs al cambio de cada cobro) · hoy 1 USD = ${Math.round(stats.tc_hoy || 0).toLocaleString("es-PY")} Gs`
              : "Tocá para ver todas las operaciones de cobro")}
            icon={TrendingUp}
            color="emerald"
            loading={loading}
            testId="kpi-total-revenue"
            delay={1}
            onAbrir={() => setDetalle("ingresos")}
          />
        )}
        {canSeeFinance && (
          <KPICard
            title="Gastos Totales"
            value={loading ? "..." : formatPYGCompact(stats?.total_expenses)}
            subtitle={loading ? "" : "Tocá para ver todos los gastos del ERP"}
            icon={TrendingDown}
            color="red"
            loading={loading}
            testId="kpi-total-expenses"
            delay={2}
            onAbrir={() => setDetalle("gastos")}
          />
        )}
        <KPICard
          title="Proyectos Activos"
          value={loading ? "..." : String(stats?.active_projects || 0)}
          subtitle={loading ? "" : `de ${stats?.total_projects || 0} · tocá para ver su estado`}
          icon={Building2}
          color="blue"
          loading={loading}
          testId="kpi-active-projects"
          delay={canSeeFinance ? 3 : 1}
          onAbrir={() => setDetalle("proyectos")}
        />
        <KPICard
          title="Personal Total"
          value={loading ? "..." : String(stats?.total_workers || 0)}
          subtitle="Tocá para ver el desglose por obra"
          icon={Users}
          color="purple"
          loading={loading}
          testId="kpi-total-workers"
          delay={canSeeFinance ? 4 : 2}
          onAbrir={() => setDetalle("personal")}
        />
      </div>

      {/* Chart + Alerts row */}
      <div className={`grid grid-cols-1 ${canSeeFinance ? "lg:grid-cols-3" : ""} gap-5`}>
        {/* Cash flow chart — finance roles only */}
        {canSeeFinance && (
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 fade-in-up stagger-3" data-testid="cash-flow-chart">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="font-bold text-gray-900 text-[15px]">Flujo de Caja</h3>
              <p className="text-[12px] text-gray-400 mt-0.5">Ultimos 6 meses (en millones Gs.)</p>
            </div>
            <span className="text-[11px] font-bold text-[#2E6DA4] bg-blue-50 px-2.5 py-1 rounded-lg border border-blue-100/80">PYG</span>
          </div>
          {loading ? (
            <div className="h-56 bg-gray-50 rounded-xl animate-pulse" />
          ) : (
            <ResponsiveContainer width="100%" height={230}>
              <AreaChart data={stats?.cash_flow || []} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorIngresos" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2E6DA4" stopOpacity={0.12} />
                    <stop offset="95%" stopColor="#2E6DA4" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorGastos" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#EF4444" stopOpacity={0.08} />
                    <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis dataKey="month" tick={{ fill: "#9CA3AF", fontSize: 11, fontWeight: 500 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}M`} width={45} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: "11px", color: "#6B7280" }} formatter={(v) => v === "ingresos" ? "Ingresos" : "Gastos"} />
                <Area type="monotone" dataKey="ingresos" stroke="#2E6DA4" strokeWidth={2.5} fill="url(#colorIngresos)" dot={{ fill: "#2E6DA4", r: 3, strokeWidth: 0 }} activeDot={{ r: 5, strokeWidth: 2, stroke: "#fff" }} />
                <Area type="monotone" dataKey="gastos" stroke="#EF4444" strokeWidth={2} fill="url(#colorGastos)" dot={{ fill: "#EF4444", r: 3, strokeWidth: 0 }} activeDot={{ r: 5, strokeWidth: 2, stroke: "#fff" }} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
        )}

        {/* Fix DO (2026-09-22): acá estaba el panel de Alertas. Los avisos siguen
            llegando por la campana y por Finanzas → Alertas; este lugar ahora muestra
            todo el movimiento del ERP, avisos incluidos. */}
        <MovimientosFeed />
      </div>

      {/* Fix DO (2026-09-22): "Mover Tareas y Flujo al dashboard, desde donde se pueda
          ver el flujo y tareas de todos los módulos tal como está" (CEN, 21/09). */}
      <TareasPanel modulo="" titulo="Tareas y Flujo de Trabajo — todos los módulos" />

      {/* Projects Progress */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 fade-in-up stagger-5" data-testid="projects-progress">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-gray-900 text-[15px]">Avance de Proyectos</h3>
          {/* Fix CS (2026-09-14): "al dar click en VER TODOS nos lleva al módulo de
              Computo? está mal. Se deben visualizar el avance de TODOS LOS PROYECTOS,
              clasificarlos en LARGO ALCANCE y CORTO ALCANCE" (Heber, 14/09). */}
          <button onClick={() => setDetalle("proyectos")} className="text-[13px] text-[#2E6DA4] hover:text-[#245A8A] font-semibold flex items-center gap-1 transition-colors group" data-testid="ver-todos-proyectos">
            Ver todos <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </button>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {(stats?.projects_summary || []).map((p) => (
              <Link key={p.id} to="/projects" className="block group">
                <div className="p-4 rounded-xl border border-gray-100 hover:border-[#2E6DA4]/20 hover:shadow-lg hover:shadow-[#2E6DA4]/[0.04] transition-all duration-200 hover:-translate-y-0.5" data-testid="project-card">
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-[11px] font-mono text-gray-400 font-medium">{p.code}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold capitalize ${getStatusColor(p.status)}`}>
                      {p.status}
                    </span>
                  </div>
                  <p className="text-[13px] font-semibold text-gray-800 leading-snug mb-3 line-clamp-2">{p.name}</p>
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-[11px] text-gray-500">
                      <span>Avance</span>
                      <span className={`font-bold ${p.progress >= 90 ? "text-red-600" : p.progress >= 70 ? "text-amber-600" : "text-emerald-600"}`}>
                        {p.progress}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                      <div
                        className={`h-1.5 rounded-full progress-bar ${getProgressColor(p.progress)}`}
                        style={{ width: `${p.progress}%` }}
                      />
                    </div>
                    <p className="text-[11px] text-gray-400 font-medium">
                      {formatPYGCompact(p.spent_pyg)} / {formatPYGCompact(p.budget_pyg)}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Recent Transactions — finance roles only */}
      {canSeeFinance && (
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden fade-in-up stagger-6" data-testid="recent-transactions">
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-gray-50">
          <h3 className="font-bold text-gray-900 text-[15px]">Transacciones Recientes</h3>
          <Link to="/finance" className="text-[13px] text-[#2E6DA4] hover:text-[#245A8A] font-semibold flex items-center gap-1 transition-colors group">
            Ver todas <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </div>
        {loading ? (
          <div className="p-5 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50/60">
                  <th className="text-left px-5 sm:px-6 py-3 text-[11px] font-bold text-gray-400 uppercase tracking-wider">Fecha</th>
                  <th className="text-left px-5 sm:px-6 py-3 text-[11px] font-bold text-gray-400 uppercase tracking-wider hidden sm:table-cell">Proyecto</th>
                  <th className="text-left px-5 sm:px-6 py-3 text-[11px] font-bold text-gray-400 uppercase tracking-wider">Tipo</th>
                  <th className="text-left px-5 sm:px-6 py-3 text-[11px] font-bold text-gray-400 uppercase tracking-wider hidden md:table-cell">Categoria</th>
                  <th className="text-left px-5 sm:px-6 py-3 text-[11px] font-bold text-gray-400 uppercase tracking-wider hidden lg:table-cell">Descripcion</th>
                  <th className="text-right px-5 sm:px-6 py-3 text-[11px] font-bold text-gray-400 uppercase tracking-wider">Monto</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {(stats?.recent_transactions || []).map((txn, i) => (
                  <tr key={txn._id} className="table-row-hover" data-testid="transaction-row">
                    <td className="px-5 sm:px-6 py-3.5 text-gray-500 whitespace-nowrap text-[13px]">{formatDate(txn.date)}</td>
                    <td className="px-5 sm:px-6 py-3.5 hidden sm:table-cell">
                      <span className="font-mono text-[11px] text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded">{txn.project_id?.slice(-6) || "-"}</span>
                    </td>
                    <td className="px-5 sm:px-6 py-3.5">
                      <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full ${
                        txn.type === "ingreso" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"
                      }`}>
                        {txn.type === "ingreso" ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                        {txn.type === "ingreso" ? "Ingreso" : "Gasto"}
                      </span>
                    </td>
                    <td className="px-5 sm:px-6 py-3.5 text-gray-500 hidden md:table-cell text-[13px]">{txn.category}</td>
                    <td className="px-5 sm:px-6 py-3.5 text-gray-400 hidden lg:table-cell max-w-xs truncate text-[13px]">{txn.description}</td>
                    <td className={`px-5 sm:px-6 py-3.5 text-right font-bold tabular-nums whitespace-nowrap text-[13px] ${
                      txn.type === "ingreso" ? "text-emerald-700" : "text-red-600"
                    }`}>
                      {txn.type === "ingreso" ? "+" : "-"}{formatPYGCompact(txn.amount_pyg)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!stats?.recent_transactions?.length && (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <Clock size={28} className="mb-2 text-gray-300" />
                <p className="text-sm font-medium text-gray-400">Sin transacciones registradas</p>
              </div>
            )}
          </div>
        )}
      </div>
      )}
      {/* Fix CS (2026-09-14): el detalle de la tarjeta que se tocó. */}
      {detalle && <DetalleDashboardModal tipo={detalle} onClose={() => setDetalle(null)} />}
    </div>
  );
}
